<!-- eslint-disable -->
<template>
  <v-container class="users-container">
    <v-row>
      <v-col cols="12">
        <div class="mb-6">
          <h1 class="text-h4">{{ $t('users.searchTitle') }}</h1>
          <p class="text-subtitle-1 text-grey">
            {{ totalUsers > 0 ? $t('users.searchSubtitleWithCount', { count: totalUsers }) : $t('users.searchSubtitle') }}
          </p>
        </div>

        <!-- Search Input -->
        <v-text-field
          v-model="searchQuery"
          :placeholder="$t('users.searchPlaceholder')"
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          clearable
          class="mb-4"
          @update:model-value="debouncedSearch"
          @click:clear="clearSearch"
        ></v-text-field>

        <!-- Loading -->
        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>

        <!-- User List -->
        <template v-else>
          <v-card elevation="2">
            <v-list>
              <template v-for="(user, index) in users" :key="user.userId">
                <v-list-item class="py-3">
                  <!-- Avatar - clickable to profile -->
                  <template v-slot:prepend>
                    <v-avatar 
                      :color="user.avatarUrl ? undefined : 'primary'" 
                      size="48" 
                      class="cursor-pointer"
                      @click="goToProfile(user.username)"
                    >
                      <v-img v-if="user.avatarUrl" :src="user.avatarUrl"></v-img>
                      <span v-else class="text-white">{{ getInitials(user) }}</span>
                    </v-avatar>
                  </template>

                  <!-- Name and username - clickable to profile -->
                  <v-list-item-title 
                    class="font-weight-medium cursor-pointer user-name"
                    @click="goToProfile(user.username)"
                  >
                    {{ user.displayName || user.username }}
                    <v-chip 
                      v-if="user.isOnline" 
                      size="x-small" 
                      color="success" 
                      class="ml-2"
                    >
                      {{ $t('profile.online') }}
                    </v-chip>
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    @{{ user.username }}
                    <span v-if="user.memberSince" class="ml-2 text-caption">
                      · {{ $t('users.memberSince') }} {{ formatDate(user.memberSince) }}
                    </span>
                  </v-list-item-subtitle>

                  <!-- Actions -->
                  <template v-slot:append>
                    <div class="d-flex align-center" style="gap: 0.5rem;">
                      <!-- Friend status / Add friend -->
                      <template v-if="user.userId !== currentUserId">
                        <v-chip 
                          v-if="user.friendStatus === 'friends'" 
                          color="success" 
                          size="small"
                          variant="tonal"
                        >
                          <v-icon start size="small">mdi-account-check</v-icon>
                          {{ $t('friends.friends') }}
                        </v-chip>
                        <v-chip 
                          v-else-if="user.friendStatus === 'pending'" 
                          color="warning" 
                          size="small"
                          variant="tonal"
                        >
                          <v-icon start size="small">mdi-clock-outline</v-icon>
                          {{ $t('friends.requestSent') }}
                        </v-chip>
                        <v-btn
                          v-else
                          size="small"
                          variant="tonal"
                          color="primary"
                          @click="sendFriendRequest(user)"
                          :loading="user.addingFriend"
                        >
                          <v-icon start size="small">mdi-account-plus</v-icon>
                          <span class="hidden-sm-and-down">{{ $t('friends.addFriend') }}</span>
                        </v-btn>

                        <!-- Send message -->
                        <v-btn
                          size="small"
                          variant="tonal"
                          color="secondary"
                          @click="startChat(user)"
                          :loading="user.startingChat"
                          :title="$t('chat.sendMessage')"
                        >
                          <v-icon size="small">mdi-message</v-icon>
                        </v-btn>
                      </template>
                      <template v-else>
                        <v-chip size="small" color="primary" variant="tonal">
                          {{ $t('users.you') }}
                        </v-chip>
                      </template>
                      
                      <!-- View profile -->
                      <v-btn
                        size="small"
                        variant="text"
                        @click="goToProfile(user.username)"
                      >
                        <v-icon size="small">mdi-chevron-right</v-icon>
                      </v-btn>
                    </div>
                  </template>
                </v-list-item>
                <v-divider v-if="index < users.length - 1"></v-divider>
              </template>

              <!-- Empty state -->
              <v-list-item v-if="users.length === 0">
                <v-list-item-title class="text-center text-grey py-8">
                  {{ searchQuery ? $t('users.noResults') : $t('users.noUsers') }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card>

          <!-- Pagination -->
          <div v-if="totalPages > 1" class="d-flex justify-center mt-4">
            <v-pagination
              v-model="currentPage"
              :length="totalPages"
              :total-visible="7"
              @update:model-value="loadUsers"
            ></v-pagination>
          </div>

          <!-- Showing info -->
          <p v-if="totalUsers > 0" class="text-center text-caption text-grey mt-2">
            {{ $t('users.showing', { from: showingFrom, to: showingTo, total: totalUsers }) }}
          </p>
        </template>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, onMounted, inject } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import api from '../services/api'

const router = useRouter()
const store = useStore()
const { t, locale } = useI18n()

const openChatWithUser = inject('openChatWithUser', null)

const searchQuery = ref('')
const users = ref([])
const loading = ref(false)
const totalUsers = ref(0)
const currentPage = ref(1)
const perPage = 20

const currentUserId = computed(() => store.getters['auth/user']?.userId)

const totalPages = computed(() => Math.ceil(totalUsers.value / perPage))
const showingFrom = computed(() => totalUsers.value === 0 ? 0 : (currentPage.value - 1) * perPage + 1)
const showingTo = computed(() => Math.min(currentPage.value * perPage, totalUsers.value))

let searchTimeout = null

const debouncedSearch = () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    currentPage.value = 1
    loadUsers()
  }, 300)
}

const clearSearch = () => {
  searchQuery.value = ''
  currentPage.value = 1
  loadUsers()
}

const loadUsers = async () => {
  loading.value = true

  try {
    const params = {
      page: currentPage.value,
      limit: perPage
    }
    
    if (searchQuery.value && searchQuery.value.length >= 2) {
      params.search = searchQuery.value
    }

    const response = await api.get('/users', { params })
    
    users.value = (response.data.users || []).map(u => ({
      ...u,
      addingFriend: false,
      startingChat: false
    }))
    totalUsers.value = response.data.total || response.data.users?.length || 0
    
  } catch (error) {
    console.error('Error loading users:', error)
    users.value = []
  } finally {
    loading.value = false
  }
}

const getInitials = (user) => {
  const name = user.displayName || user.username
  return name.charAt(0).toUpperCase()
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleDateString(locale.value, { month: 'short', year: 'numeric' })
}

const goToProfile = (username) => {
  router.push(`/profile/${username}`)
}

const sendFriendRequest = async (user) => {
  user.addingFriend = true
  try {
    await api.post('/friends', { userId: user.userId })
    user.friendStatus = 'pending'
  } catch (error) {
    console.error('Error sending friend request:', error)
    alert(error.response?.data?.error || t('common.error'))
  } finally {
    user.addingFriend = false
  }
}

const startChat = async (user) => {
  user.startingChat = true
  try {
    // Create or get existing conversation
    const response = await api.post('/conversations', { userId: user.userId })
    const conversation = response.data.conversation
    
    // Open chat widget if available
    if (openChatWithUser) {
      openChatWithUser({
        odernUserId: user.userId,
        username: user.username,
        displayName: user.displayName || user.username,
        avatarUrl: user.avatarUrl
      })
    } else {
      // Fallback: navigate to chat view
      router.push('/chat')
    }
  } catch (error) {
    console.error('Error starting chat:', error)
  } finally {
    user.startingChat = false
  }
}

onMounted(() => {
  loadUsers()
})
</script>

<style scoped>
.users-container {
  max-width: 900px;
  margin: 0 auto;
  padding-top: 2rem;
}

.cursor-pointer {
  cursor: pointer;
}

.user-name:hover {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
}
</style>